import { AfterViewInit, Component, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  ScheduledEvent,
  WaitlistEntry,
  WaitlistEntryStatus
} from '../../../../../../../../../models/scheduled-event';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../../../../utils';
import { LocalStorage } from 'angular-web-storage';
import { ExportEventWaitlistModalComponent } from '../../../../../modals/export-event-waitlist-modal/export-event-waitlist-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddToEventWaitlistModalComponent } from './modals/add-to-event-waitlist-modal/add-to-event-waitlist-modal.component';
import { openClimberAppInNewTabAsCustomer, openClimberAppInNewTabAsStaff } from '../../../../../../../../../utils-models';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { InviteWaitlistEntryToBookModalComponent } from './modals/invite-waitlist-entry-to-book-modal/invite-waitlist-entry-to-book-modal.component';
import { ModifyPendingWaitlistEntryModalComponent } from './modals/modify-waitlist-entry-booking-deadline-modal/modify-pending-waitlist-entry-modal.component';
import { ResendPendingWaitlistInvitationsModalComponent } from './modals/resend-pending-waitlist-invitations-modal/resend-pending-waitlist-invitations-modal.component';
import {
  Customer,
  CustomerActiveProficiency,
  CustomerMembershipIcon,
  CustomerProfileDetails
} from '../../../../../../../../../models/customer';
import {
  SimpleMessageModalComponent
} from '../../../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { ScheduledEventService } from '../../../../../../../../../services/scheduled-event/scheduled-event.service';


@Component({
  selector: 'app-event-waitlist-sub-list',
  templateUrl: './event-waitlist-sub-list.component.html',
  styleUrls: ['./event-waitlist-sub-list.component.css']
})
export class EventWaitlistSubListComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() event: ScheduledEvent;
  @Input() resultCallback: (result: CallbackResult, reload: boolean) => void;

  @ViewChild('waitlistTableWrapper') waitlistTableWrapper;
  @ViewChild('waitlistTableTitleContainer') waitlistTableTitleContainer;

  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other
  // similar variables.
  @LocalStorage() public pageSizeEventWaitlistSubList;
  public search = '';
  public onlyShowWaitingAndPending = false;

  public customer: {[id: number]: Customer} = {};
  public customerProfileDetails: {[id: number]: CustomerProfileDetails} = {};
  public loadingCustomer: {[id: number]: boolean} = {};
  public loadingCustomerProfileDetails: {[id: number]: boolean} = {};
  public customerNotFound: {[id: number]: boolean} = {};

  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;
  public SEStatus = WaitlistEntryStatus;

  constructor(
    private scheduledEventService: ScheduledEventService,
    private customerService: CustomerService,
    private orgService: OrganizationConfigService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.pageSizeEventWaitlistSubList) {
      this.pageSizeEventWaitlistSubList = 10;
    }
    // This is needed in order for the variable to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeEventWaitlistSubList = this.pageSizeEventWaitlistSubList;

    this.loadWaitlistCustomerInfo(this.event);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.loadWaitlistCustomerInfo(changes.event.currentValue);
  }

  ngAfterViewInit() {
    this.updateTableTitleContainerWidth();
    // Initial call can make the title container a little too small - might be
    // related to the scrollbar?
    setTimeout(
      () => { this.updateTableTitleContainerWidth(); },
      100
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.updateTableTitleContainerWidth();
  }

  public filteredWaitlistEntries(): WaitlistEntry[] {
    const filterItems = this.search.toLowerCase().split(' ');

    return this.event.waitlist_entries.filter(we => {
      if (this.onlyShowWaitingAndPending) {
        const Stat = WaitlistEntryStatus;
        if (![Stat.waiting, Stat.pending].includes(we.status)) {
          return false;
        }
      }

      for (const filter of filterItems) {
        const filterMatches = (
          (we.first_name + ' ' + we.last_name).toLowerCase().includes(filter) ||
          we.notification_email.toLowerCase().includes(filter)
        );
        if (!filterMatches) { return false; }
      }

      return true;
    }).slice(
      (this.pageNumber - 1) * this.pageSizeEventWaitlistSubList,
      this.pageNumber * this.pageSizeEventWaitlistSubList
    );
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    return Math.ceil(
      this.event.waitlist_entries.length / this.pageSizeEventWaitlistSubList
    );
  }

  public openExportWaitlistModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(ExportEventWaitlistModalComponent);
    modalRef.componentInstance.waitlistEntries = this.event.waitlist_entries;
    modalRef.componentInstance.savedCallback = () => {
      this.resultCallback(
        {
          text: 'Waitlist exported successfully.',
          status: CallbackResultStatus.success
        },
        false
      );
    };
  }

  public openAddToWaitlistModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(AddToEventWaitlistModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.addedToWaitlistCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    };
  }

  public handleCompleteBooking(entry: WaitlistEntry) {
    if (entry.booking_customer_id === null) {
      this.customerService
        .getStaffClimberAppToken()
        .then(token => {
          openClimberAppInNewTabAsStaff(
            token,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.event.event_schedule_from_waitlist_url
              + entry.id
              + '/?use_saved_response=false'
            )
          );
        })
        .catch(reason => {
          this.serverError = reason.status;
        });
    } else {
      this.customerService
        .getCustomerClimberAppToken(entry.booking_customer_id)
        .then(token => {
          openClimberAppInNewTabAsCustomer(
            token,
            entry.booking_customer_id,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.event.event_schedule_from_waitlist_url
              + entry.id
              + '/?use_saved_response=false'
            )
          );
        })
        .catch(reason => {
          this.serverError = reason.status;
        });
    }
  }

  public openInviteToBookModal(entry: WaitlistEntry) {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(InviteWaitlistEntryToBookModalComponent);
    modalRef.componentInstance.waitlistScheduledEvent = this.event;
    modalRef.componentInstance.waitlistEntry = entry;
    modalRef.componentInstance.inviteSentCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    };
  }

  public pendingWaitlistEntries(): WaitlistEntry[] {
    return this.event.waitlist_entries
      .filter(we => we.status === WaitlistEntryStatus.pending);
  }

  public openResendPendingInvitationsModal() {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(ResendPendingWaitlistInvitationsModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.pendingEntries = this.pendingWaitlistEntries();
    modalRef.componentInstance.resentCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    };
  }

  public openRemoveWaitlistEntryModal(entry: WaitlistEntry) {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Waitlist Entry';
    modalRef.componentInstance.messageText = (
      'Are you sure you want to remove the wait list entry for ' +
      entry.first_name + ' ' + entry.last_name +
      '? This action cannot be undone.'
    );
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      if (entry.participant_customer_id) {
        this.loadingCustomer[entry.participant_customer_id] = true;
        this.loadingCustomerProfileDetails[entry.participant_customer_id] = true;
      }
      this.scheduledEventService
        .removeWaitlistEntry(entry.id)
        .then(() => {
          this.resultCallback(
            {
              text: (
                'Waitlist entry for ' +
                entry.first_name + ' ' + entry.last_name +
                ' removed successfully.'
              ),
              status: CallbackResultStatus.success
            },
            true
          );
        })
        .catch(reason => {
          let errorMessage = '';
          if (reason.status === 403) {
            errorMessage = 'You do not have permission to remove this waitlist entry.'
          } else if (reason.status === 400) {
            errorMessage = reason.error;
          } else {
            errorMessage = 'Error removing waitlist entry: ' + reason.status.toString();
          }
          this.resultCallback(
            {
              text: errorMessage,
              status: CallbackResultStatus.error
            },
            true
          );
        });
    };
  }

  public openModifyPendingEntryModal(entry: WaitlistEntry) {
    this.resultCallback(null, false);

    const modalRef = this.modalService.open(ModifyPendingWaitlistEntryModalComponent);
    modalRef.componentInstance.scheduledEvent = this.event;
    modalRef.componentInstance.waitlistEntry = entry;
    modalRef.componentInstance.actionCompleteCallback = (result: CallbackResult) => {
      this.resultCallback(
        result,
        true
      );
    };
  }

  /**
   * The waitlist may only be added to if the event is full, or if there are
   * already active waitlist entries.
   */
  public addToWaitlistAllowed() {
    return (
      (this.event.remaining_capacity <= 0) ||
      (
        this.event.waitlist_entries
          .filter(we => [WaitlistEntryStatus.waiting,
            WaitlistEntryStatus.pending].includes(we.status))
          .length > 0
      )
    )
  }

  public getCustomerProficienciesForId(customerIdInt: number): CustomerActiveProficiency[] {
    if (!this.customerProfileDetails[customerIdInt]) {
      return [];
    }
    return this.customerProfileDetails[customerIdInt].active_proficiencies;
  }

  public getCustomerMembershipIconsForId(customerIdInt: number): CustomerMembershipIcon[] {
    if (!this.customerProfileDetails[customerIdInt]) {
      return [];
    }
    return this.customerProfileDetails[customerIdInt].membership_icons;
  }

  private loadWaitlistCustomerInfo(event: ScheduledEvent) {
    for (const entry of event.waitlist_entries) {
      if (entry.participant_customer_id) {
        this.loadCustomer(entry.participant_customer_id);
        this.loadCustomerProfileDetails(entry.participant_customer_id);
      }
    }
  }

  /**
   * Load the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   */
  private loadCustomer(customerIdInt: number) {
    this.loadingCustomer[customerIdInt] = true;

    this.customerService
      .getCustomer(customerIdInt)
      .then(customer => {
        this.customer[customerIdInt] = customer;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.customerNotFound[customerIdInt] = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomer[customerIdInt] = false; });
  }

  /**
   * Load the profile details of the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   */
  private loadCustomerProfileDetails(customerIdInt: number) {
    this.loadingCustomerProfileDetails[customerIdInt] = true;

    this.customerNotFound[customerIdInt] = false;

    this.customerService
      .getCustomerProfileDetails(customerIdInt)
      .then(customerProfileDetails => {
        this.customerProfileDetails[customerIdInt] = customerProfileDetails;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.customerNotFound[customerIdInt] = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingCustomerProfileDetails[customerIdInt] = false;
      });
  }

  private updateTableTitleContainerWidth() {
    this.waitlistTableTitleContainer.nativeElement.style.width =
      this.waitlistTableWrapper.nativeElement.offsetWidth.toString() + 'px';
  }
}
